import type { Ref } from "vue";

type RedditEventNames =
  | "ViewContent"
  | "Purchase"
  | "SignUp"
  | "AddToWishlist"
  | "PageVisit"
  | "AddToCart";
type RedditEvent = [RedditEventNames, any?];

/* eslint-disable */
function injectPreScript(w: any, d: any) {
  // this piece of code is generated by Reddit, don't try and understand it

  if (!w.rdt) {
    var p: any = (w.rdt = function () {
      p.sendEvent
        ? p.sendEvent.apply(p, arguments)
        : p.callQueue.push(arguments);
    });
    p.callQueue = [];
  }
}
/* eslint-enable */

const _useRedditPixel = () => {
  const redditEventQueue: Ref<RedditEvent[]> = ref([]);
  const { user } = useUser();
  const { $segment, $cookieConsent } = useNuxtApp();
  const $config = useRuntimeConfig();
  const redditPixelEnabled = $config.public.reddit.enabled;
  const redditPixelId = $config.public.reddit.pixelId;
  const redditPixelLoaded = ref(false);
  const platformStore = usePlatformStore();
  const isMatchWornShirt = computed(() => platformStore.isMatchWornShirt);

  if (import.meta.client) {
    injectPreScript(window, document);
  }

  const trigger = computed(() => {
    return (
      isMatchWornShirt.value &&
      !!import.meta.client &&
      redditPixelEnabled &&
      $cookieConsent?.consent?.marketing
    );
  });

  // watch here
  const { onLoaded } = useScript(
    {
      src: "https://www.redditstatic.com/ads/pixel.js",
      defer: true,
      async: true,
    },
    {
      trigger,
    },
  );

  onLoaded(() => {
    (window as any).rdt("init", redditPixelId);
    redditPixelLoaded.value = true;

    // if we had any track events fired before, we can send them now
    flushQueue(redditEventQueue);
  });

  const getUserParams = async () => {
    if (user.value) {
      return {
        // Only advance matching parameters that Reddit supports
        email: user.value.email,
        phoneNumber: user.value.phoneNumber,
        externalId: user.value.id,
      };
    }

    const segmentUser = await $segment.browser.value.user();

    return { externalId: segmentUser?.anonymousId() };
  };

  const trackRedditEvent = async (
    event: RedditEventNames,
    payload?: Record<string, any>,
  ) => {
    if (
      typeof (window as any) === "undefined" ||
      !import.meta.client ||
      !isMatchWornShirt.value ||
      !redditPixelEnabled
    ) {
      return;
    }

    if (redditPixelLoaded.value && typeof (window as any).rdt === "function") {
      const userParams = await getUserParams();
      // pixel is loaded, fire the event
      (window as any).rdt("track", event, {
        ...userParams,
        ...(payload || {}),
      });
    } else {
      // pixel is not loaded yet, add event to queue
      redditEventQueue.value.push([event, payload]);
    }
  };

  const flushQueue = async (redditEventQueue: Ref<RedditEvent[]>) => {
    const userParams = await getUserParams();
    while (redditEventQueue.value.length > 0) {
      const [queuedEvent, queuedPayload] = redditEventQueue.value.shift()!;
      (window as any).rdt(
        "track",
        queuedEvent,
        queuedPayload
          ? JSON.parse(
              JSON.stringify({
                ...userParams,
                ...(queuedPayload || {}),
              }),
            )
          : {},
      );
    }
  };

  return { trackRedditEvent };
};

export default createSharedComposable(_useRedditPixel);
